import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  usePharmacyGenericNames,
  useCreatePharmacyGenericName,
  useUpdatePharmacyGenericName,
  useDeletePharmacyGenericName,
  useBulkDeletePharmacyGenericNames,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { PharmacyLookupPage } from './PharmacyLookupPage';
import { FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateLookupDto } from '../../services/httpServices/pharmacyDrugService';

export function PharmacyGenericNames() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const limit = 20;

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<CreateLookupDto>({ name: '', description: '', isActive: true });

  const { data, isLoading, error } = usePharmacyGenericNames(page, limit, debouncedSearch || undefined);
  const meta = data?.meta;
  const items = data?.data || [];

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const createMutation = useCreatePharmacyGenericName();
  const updateMutation = useUpdatePharmacyGenericName();
  const deleteMutation = useDeletePharmacyGenericName();
  const bulkDeleteMutation = useBulkDeletePharmacyGenericNames();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      await updateMutation.mutateAsync({ id: editingItem.id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setModalOpen(false);
    setFormData({ name: '', description: '', isActive: true });
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleBulkDelete = async (selected: any[]) => {
    if (selected.length === 0) return;
    try {
      await bulkDeleteMutation.mutateAsync(selected.map((item) => item.id));
      toast.success(
        `Deleted ${selected.length} generic name${selected.length === 1 ? '' : 's'}`,
      );
    } catch {
      toast.error('Failed to delete some generic names');
    }
  };

  return (
    <PharmacyLookupPage
      title="Generic Names"
      description="Manage generic (active ingredient) names of medicines"
      icon={FlaskConical}
      items={items}
      isLoading={isLoading}
      error={error}
      search={search}
      setSearch={setSearch}
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      editingItem={editingItem}
      setEditingItem={setEditingItem}
      formData={formData}
      setFormData={setFormData}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isSubmitting={createMutation.isPending || updateMutation.isPending}
      isDeleting={deleteMutation.isPending}
      onBulkDelete={handleBulkDelete}
      isBulkDeleting={bulkDeleteMutation.isPending}
      extraFields={[
        { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' },
      ]}
      page={page}
      limit={limit}
      total={meta?.total || 0}
      totalPages={meta?.totalPages || 1}
      onPageChange={setPage}
      itemName="generic names"
    />
  );
}
